<template>
  <div class="post-content">
    <div class="row row--flex">
      <div class="col-70">
        <p>For every printing project, choosing the right type of paper, or medium in general, is key. Sustainable and environment-conscious choices don’t mean that you’ll have to used brown or grey recycled paper that is difficult to print on and has low print readability. Those papers can be very appealing and draw attention to the fact that we are using “ecological” solutions, but are also a kind of curiosity, usually much more expensive than regular paper. My goal here is to show various choices of white paper fit for job-printings (business cards, invitations), and high volume print (leaflets, catalogues).</p>
      </div>
      <Sidenote class="col-30">
        <p>My goal here is to show various choices of white paper fit for job-printings (business cards, invitations), and high volume print (leaflets, catalogues).</p>
      </Sidenote>
    </div>
    <div class="row">
      <div class="col-70">
        <p>Recycled paper is not the most sustainable choice we can make. Paper can be recycled up to 6 times, and using the paper from the next phase (usually the second one) is not unequivocally better than its first, white and fresh incarnation. What’s important is how high-quality is the primary product and whether our production process doesn’t stop it from being recycled further.</p>
      </div>
    </div>
    <div class="row row--flex">
      <div class="col-70">
        <p style="margin-bottom: 25px;">When it comes to white papers, the choice is simple:</p>
        <ul>
          <li>choose uncoated papers</li>
          <li>choose certified papers</li>
          <li>choose papers with the lowest possible carbon footprint</li>
        </ul>
      </div>
    </div>
    <div class="row">
      <div class="col-70">
        <p class="heading--sans-serif">Choose uncoated papers</p>
        <p>Uncoated paper is less harmful to the environment. Popular and cheap oated paper is made of cellulose core, coated from both sides with calcium carbonate with addition of bleaching agents and other harmful substances. Coated papers, especially brandless ones, may include toxins, and even microplastics. Retrieving paper pulp from the coated paper in the recycling process is very difficult. In our comparison of various paper types, you will also find high-quality coated papers that can be a good alternative to the calcium carbonate-coated paper if you really want to use this type of medium for your project.</p>
      </div>
    </div>
    <div class="row">
      <div class="col-70">
        <p class="heading--sans-serif">Choose certified papers</p>
        <p>Paper quality certificates allow an objective assessment of the product’s origin. However, the multitude of certificates we encounter on the market, along with certificates and assessment criteria introduced by paper distributors can lead to some confusion. In order to offer you a clear and simple comparison, I checked sustainability certification (FSC), and chlorine bleaching level (the ideal being TFC – Total Chlorine Free status). I also took into account other certificates, such as the EU Eco Label.</p>
      </div>
    </div>
    <div class="row">
      <div class="col-70">
        <p class="heading--sans-serif">Certificates:</p>
        <p class="certificate">FSC</p>
        <p>The Forest Stewardship Council (FSC) is an international non-profit organisation, established by environmental groups, businesses, and trade unions to protect sustainable forestry with unified global standards. FTC certification is ver stringent, which is why I chose it over the similar, but not exactly equal PEFC (Programme for the Endorsement of Forest Certification). Both systems aim for sustainable forest management and overlap in many aspects. They share some of their certification criteria, such as:</p>
      </div>
    </div>
    <div class="row">
      <div class="col-70">
        <ul>
          <li>Operational planning in proportion to the forest size</li>
          <li>Sustainable management level</li>
          <li>Protection of biodiversity, especially in protected areas</li>
          <li>Providing diverse and high-quality products with best-possible use of natural resources</li>
          <li>Choosing local tree species</li>
        </ul>
      </div>
    </div>
    <div class="row">
      <div class="col-70">
        <p>The difference between those certificates lies in details. In FSC, three chambers decide on the certification criteria: representatives of environmental associations, social benefit groups, and business groups. Meanwhile, PEFC – as they claim themselves – focuses mainly on protecting the interests of forest owners. FSC standards are much more detailed, especially when it comes to individual ecological criteria. For example, FSC requires that 5% of the forest is not cultivated and remains a reference area, and ten trees in every hectare must be protected as habitat trees in the long-term perspective. The use of chemical pesticides in the FCS forests is only allowed based on the official order, while in the PEFC-certified forests, it is limited to the “necessary amount”. There are many such small differences regarding forest-planning as a whole, including the width and location of paths on which industrial machines can drive.</p>
      </div>
    </div>
    <div class="row">
      <div class="col-70">
        <p>PEFC pays much attention to simple and inexpensive certification procedures. While in FSC, every forestry company (including every forest owner or group of owners) is undergoing annual controls, PEFC – especially within the regional certification procedures – can certify large areas at once, thus significantly simplifying the whole certification process for individual owners. PEFC requires annual controlling of at least 10% of the certified forest area. This means less effort, leading to lower expenditures. In other words, as I looked for unambiguously safe certification, I chose a reliable “classic” that is the more restrictive FSC.</p>
      </div>
    </div>
    <div class="row">
      <div class="col-70">
        <p style="margin-bottom: 25px;">There are three types of FSC certificates:</p>
        <ul>
          <li>FSC 100% label, meaning the wood used to make the product comes exclusively from FSC-certified forests. The organisation reports that one-third of all FSC-certified products have the FSC 100% label.</li>
          <li>FSC Mix label means the wood used to manufacture the product comes from FSC-certified materials, recycled paper, or controlled wood schemes.</li>
        </ul>
      </div>
    </div>
    <div class="row">
      <div class="col-70">
        <p>The above list is not the full certification within the FSC system. FSC-controlled wood is not certified, but it cannot be obtained illegally and with any breaches of common or civil law. It also must not be obtained from the forests whose unique natural value is endangered, in the forests turned plantations, and where genetically modified trees are grown.</p>
        <ul style="margin: 25px 0;">
          <li>FSC Recycled label means that all timber or paper used in the manufacturing of the product comes from recycled materials.</li>
        </ul>
        <a
          class="source-link source-link--right"
          href="https://pl.fsc.org/pl"
          target="_blank"
          rel="noopener noreferrer"
        >źródło: pl.fsc.org</a>
      </div>
    </div>
    <div class="row">
      <div class="col-70">
        <p>
          It’s also worth noting that in some paper mills, we can order paper with or without the FSC certificate. This means that on our request, the paper mill can apply for a certain type of certification and request a certification number. Such a procedure comes with extra effort and external costs, which is why the paper that receives the certification on request can be more expensive. However, it doesn’t mean the certificate can be “bought” if the product does not meet the strict FSC criteria. The whole list of issued certificates can be found on the FSC website.
          <span
            class="text-background"
          >Before placing the FSC logo on any products of materials, always make sure you have the right to do so – discuss it with your paper mill or ask your printer for more information.</span>
        </p>
      </div>
    </div>
    <div class="row">
      <div class="col-70">
        <p class="certificate">ECF/TCF/PCF – how does chlorine affect us?</p>
        <p>Paper is made of cellulose pulp. It needed to be bleached for the paper not to go yellow (if you remember the cheap, high-print-run books from the 1980s, you surely know what I’m talking about). Until the 1990s, the most common bleach of choice was chlorine.</p>
      </div>
    </div>
    <div class="row">
      <div class="col-70">
        <p>Bleaching with elemental chlorine (EC) means the lignin present in paper pulp becomes soluble in water. Next, chlorine needs to be separated from the ligninę by leaching. Processed chlorine quickly produces organic compounds and dioxins. They are then introduced to the ecosystem, along with the rest of the paper industry sewage. What does this mean for humans? Such organic compounds are quickly absorbed by living organisms and are stored in fatty tissues, leading to damaging of such organs as liver or kidneys. In the 1980s, non-chlorine bleaching process was invented and named TCF (total chlorine-free). The TCF label indicates that both paper and pulp were bleached entirely without chlorine. This is the safest solution. Another option is the less restrictive ECF (elemental chlorine-free), meaning paper production without the use of any assimilable elemental chlorine.</p>
      </div>
    </div>
    <div class="row">
      <div class="col-70">
        <p>ECF technology reduces the amount of environmentally-harmful compounds by 60-80%. For TCF, the amounts drop below the detection limit. Yes, TCF papers are not as white as ECF, but this does not make them resemble the low-quality recycled paper. The ECF process is so prevalent in Europe that whenever we reach for some paper, we can be almost sure it’s an unmarked ECF product – however, it’s still worth double-checking. At the same time, the TCF-label papers make for about 5% of the market.</p>
      </div>
    </div>
    <div class="row">
      <div class="col-70">
        <p class="heading--sans-serif">Labels:</p>
        <p>
          <span class="certificate--indend">
            TCF
            <span>(total chlorine free)</span>
          </span>
          During the bleaching process, no chlorine gas, chlorine dioxide, or hypochlorite compounds are used. Instead, oxygen compounds are applied, such as ozone and hydrogen peroxide
          <span
            class="certificate--indend"
          >
            ECF
            <span>(elementar chlorine free)</span>
          </span>
          does not allow the use of chlorine gas, but does allow the use of chlorine compounds.
          <span
            class="certificate--indend"
          >
            PCF
            <span>(process chlorine free)</span>
          </span>
          used for recycled paper. Since waste paper is not considered raw material regardless of whether the initial product did or did not contain chlorine, this label is only used to indicate the paper was not bleached again.
        </p>
      </div>
    </div>
    <div class="row">
      <div class="col-70">
        <p class="certificate">EU ECOLABEL</p>
        <p>EU Ecolabel is a certificate issued by the European Commission for products and services that meet high environmental standards across their entire lifecycle – from the sourcing of materials, through production and distribution, to waste management. The EU Ecolabel promotes the closed-chain economy, encouraging companies to generate less waste and CO2 during their production process. The EU ecological certification scheme also prompts businesses to design products that are long-lasting, easy to repair and highly recyclable.</p>
        <p>This certification can apply to a wide range of products, but let’s take a look at the “graphic paper”, used for printing jobs (which is how it’s referred to as a product).</p>
      </div>
    </div>
    <div class="row">
      <div class="col-70">
        <p>Graphic paper products with EU Ecolabel certification meet all the criteria that ensure:</p>
      </div>
    </div>
    <div class="row">
      <div class="col-70">
        <ul>
          <li>Low level of air and water pollution in the production process</li>
          <li>Limited amounts of dangerous substances used</li>
          <li>Production with the use of certified fibres from a sustainably-managed forest</li>
        </ul>
      </div>
    </div>
    <div class="row">
      <div class="col-70">
        <p>All up-to-date Eu Ecolabel certificates can be found online:</p>
        <a
          href="http://ec.europa.eu/ecat/category/en/54/graphic-paper"
          target="_blank"
          rel="noopener noreferrer"
          class="source-link"
        >ec.europa.eu/ecat/category/en/54/graphic-paper</a>
      </div>
    </div>
    <div class="row">
      <div class="col-70">
        <p class="certificate">NORDIC ECOLABEL</p>
        <p>Nordic Ecolabel (also known as the Nordic swan) is the officially registered ecological certification label for the products from the Scandinavian countries. The certification criteria include the whole life cycle of a product, from its production (the use of chemicals, pollution of air and water), to distribution and ease of utilisation. In the EU, Nordic Ecolabel is an equivalent of the EU Ecolabel, with similar issuing criteria used by both systems, and often work together.</p>
      </div>
    </div>
    <div class="row">
      <div class="col-70">
        <p class="certificate">Der Blaue Engel / Blue Angel</p>
        <p>It is a German certificate issued since the 1970s. The paper with this certificate must be at least 80% recycled. If wood fibres were used in the production process, they must come from sustainably-managed forests and forestry companies with high ecological standards in place.</p>
      </div>
    </div>
    <div class="row">
      <div class="col-70">
        <p>To read more about the labels criteria and to see the list of certified papers, visit:</p>
        <a
          href="https://www.blauer-engel.de/en/products/paper-printing/printing-and-publication-papers"
          target="_blank"
          rel="noopener noreferrer"
          class="source-link"
        >blauer-engel.de/en/products/paper-printing/printing-and-publication-papers</a>
      </div>
    </div>
    <div class="row">
      <div class="col-70">
        <p class="certificate">EMAS</p>
        <p>EMAS is a European certification that supports sustainable development and effective management of available resources and energy. EMAS-certified organisations have their duties regarding environment protection, must optimise the costs of production and effectively manage their use of energy and resources.</p>
      </div>
    </div>
    <div class="row">
      <div class="col-70">
        <p>Read more at:</p>
        <a
          href="http://emas.gdos.gov.pl/o-emas"
          target="_blank"
          rel="noopener noreferrer"
          class="source-link"
        >emas.gdos.gov.pl/o-emas</a>
      </div>
    </div>
    <div class="row">
      <div class="col-70">
        <p class="heading--sans-serif">Choose papers with a lower carbon footprint.</p>
        <p>Watch out for greenwashing. Paper producers introduce many attractive products with “green” additives that are meant to imply they are environment- friendly. Make sure those options are biodegradable (if not, they will not be available for recycling). Exotic additions to bespoke paper brands, or paper brands available and shipped only to order, can be an ecological trap. Try to buy local whenever possible, choosing from the available options. Don’t go for half-solutions.</p>
      </div>
    </div>
    <div class="row">
      <div class="col-70">
        <p class="heading--sans-serif">The simplest choices</p>
        <p>There are dozens of paper brands and types available in the market, from white to dyed to bespoke solutions. To make it easier, I prepared a list of safe papers and those not worth using.</p>
        <p style="font-weight: 400; margin: 15px 0;">White paper: the best choices:</p>
        <p
          style="font-weight: 400; margin-bottom: 15px;"
        >TFC (total chlorine-free) papers are very safe and contain no optical whiteners:</p>
        <ul>
          <li>Nautilus (except the superwhite option) from the Mondi paper mill – recycled, TFC, Blue Angel certificate.</li>
          <li>Alto Naturel and Alto Creme bulkiness 1.3 and 1.5 – FSC, TCF, EMAS, ISO 9706 and Nordic Swan Ecolabel. (by Polish brand Panta)</li>
          <li>BIO TOP 3 next from Mondi – EU Ecolabel, FSC, TCF/ECF</li>
        </ul>
        <p style="margin: 50px 0 25px;">Papers with the EU Ecolabel or Nordic Ecolabel:</p>
        <ul>
          <li>All Munken Design papers by Arctic Paper (also those distributed by Europapier) – Nordic Ecolabel, FSC, ECF, EMAS, food contact certificate. Toy safety certificate DIN EN 71-3, and the ISO 9706 archival paper permanence certification</li>
          <li>Nautilus Superwhite by the Mondi paper mill – EU Ecolabel, FSC recycled, also OHSAS and the DIN 6738 archival paper permanence certification</li>
          <li>Maxi Script by the UPM paper mill – EU Ecolabel, FSC, ECF, toy safety certificate DIN EN 71-3, and the ISO 9706 archival paper permanence certification, food contact certificate, EMAS.</li>
          <li>Soporset Premium Pre-Print by The Navigator Company paper mill – EU Ecolabel, FSC, ECF</li>
          <li>DNS Premium by the Mondi paper mill – Eu Ecolabel, FSC, ECF.</li>
          <li>IQ Selection Smooth by Mondi – EU Ecolabel, FSC, ECF, the ISO 9706 archival paper permanence certification</li>
          <li>Print Speed Offset / Laser Jet by Antalis – EU Ecolabel, FSC, ECF, the ISO 9706 archival paper permanence certification</li>
          <li>Pergraphica by Mondi – EU Ecolabel, FSC, ECF – but it should be noted that this paper contains eucalyptus fibres which are (obviously) not sourced locally</li>
          <li>The whole uncoated wood paper range from Stora: Brite Book, Bulk, Classic, Creamy, Novel 76/80, Plus Book – an interesting economy choice for large-sized book publications</li>
        </ul>
      </div>
    </div>
    <div class="row row--flex">
      <div class="col-70">
        <p class="heading--sans-serif">The worst choices</p>
        <p>
          <span
            class="text-background"
          >The options you should give a second thought are synthetic mediums, double-coated paper, and multiloft.</span> All those mediums include additives that affect their recyclability. In the case of synthetic materials, the situation is plain and simple – they are not part of the paper recycling process. With coated paper, it gets more complicated. This paper is made in a tiny part of natural components while containing plenty of glue and pigment. It is far from the best choice you can make. As for multiloft papers (a sandwich-like paper with several layers surrounding a coloured or white core), we are dealing with integrated glue on the membrane. And since it is made of many various elements, this paper is not recommended, either.
        </p>
      </div>
      <div class="col-30">
        <RelatedPosts :currentPostID="postID" />
      </div>
    </div>
    <ScrollToTop />
  </div>
</template>

<script>
import RelatedPosts from "../RelatedPosts";
import ScrollToTop from "../ScrollToTop";
import Sidenote from "../Sidenote";

export default {
  name: "Post3",
  components: {
    RelatedPosts,
    ScrollToTop,
    Sidenote
  },
  props: {
    postID: Number
  }
};
</script>

<style lang="scss">
</style>